import { HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { toAppId } from '@app/v2/shared/utils';
import { AppErrorCode } from '@core/common-constants';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { TrialLinkStatus } from '@lemans/shared';
import { ActionButtonStyles, NotificationType } from '@ra-web-tech-ui-toolkit/components';
import { ErrorHandlers } from '@servicesV2/apierror-handlers.service';
import { CommandRequestBuilderService } from '@servicesV2/command-request-builder.service';
import { CategoryName, TenantEvent, TrialEvent } from '@servicesV2/command-request.service';
import { ApiEndPoints, DataService } from '@servicesV2/data.service';
import { combineLatest, combineLatestWith, Observable, of, switchMap } from 'rxjs';
import { DomainEvent } from '../../models/domain-event';
import { ControlPageFacade, navigateTo } from '../control-page';
import { IDomainMessage } from '../lemans-app.model';
import { openWebTechDialog } from '../modal';
import { SignalRFacade } from '../signalR';
import { UiStateActions } from '../ui-state';
import { getTenantEffectiveRolesAndServices, getUserPreferencesSuccess, UserDataFacade } from '../user-data';
import { TENANT_MGMT_ROUTES } from '../user-data/user.model';
import {
  allocateTrial,
  clearTrialGridRefresh,
  getTrialReservationInfo,
  redeemTrial,
  redeemTrialSuccess,
  refreshTrialGrid,
  setTrialBundles,
  setTrialReservationInfo,
} from './trials.actions';
import { DomainResponseDataForTrialWithCSP } from './trials.model';

@Injectable({
  providedIn: 'root',
})
export class TrialsEffects {
  private readonly actions$ = inject(Actions);
  private readonly dataService = inject(DataService);
  private readonly commandRequestBuilderService = inject(CommandRequestBuilderService);
  private readonly signalRFacade = inject(SignalRFacade);
  private readonly controlPageFacade = inject(ControlPageFacade);
  private readonly userDataFacade = inject(UserDataFacade);
  private readonly router = inject(Router);
  private readonly errorHandlers = inject(ErrorHandlers);

  private readonly DASHBOARD_PATH = '/dashboard';
  private readonly ORG_ENTITLEMENT_PATH = '/entitlement';
  private readonly USER_ENTITLEMENT_PATH = '/my-entitlements';

  getTrialBundles$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getUserPreferencesSuccess),
      switchMap(() => this.dataService.getTrialBundles$()),
      switchMap((trialBundles) => {
        return [setTrialBundles({ payload: trialBundles })];
      }),
    );
  });

  getTrialReservationInfo$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getTrialReservationInfo),
      concatLatestFrom(() => this.userDataFacade.currentTenantContext$),
      switchMap(([request, tenantContext]) => {
        return combineLatest([this.dataService.getTrialReservationInfo$(request.payload), of(tenantContext)]);
      }),
      switchMap(([trialReservation, tenantContext]) => {
        let title = 'Trial already redeemed';
        let message =
          'This trial link has already been used. Please check your account trials. If you still have questions, reach out to your sales representative.';

        if (trialReservation.status === TrialLinkStatus.CANCELLED) {
          title = 'Invalid Trial Link';
          message = 'Please contact your sales representative for a new link. We apologize for the inconvenience.';
        } else if (trialReservation.status === TrialLinkStatus.LINKEXPIRED) {
          title = 'Invalid Trial Link';
          message =
            'Looks like your link has expired. Please contact your sales representative for a new link. We apologize for the inconvenience.';
        }
        const config = {
          title: title,
          message: message,
          buttons: [{ label: 'Close', buttonStyle: ActionButtonStyles.Main }],
          showCloseIconButton: true,
        };
        if (trialReservation.status !== TrialLinkStatus.UNREDEEMED) {
          let redirectToPath: string = TENANT_MGMT_ROUTES.ORGANIZATIONS;
          if (tenantContext !== 'NotSelected' && tenantContext !== 'Personal') {
            redirectToPath = this.DASHBOARD_PATH;
          }
          return [
            openWebTechDialog({ config: config, notificationType: NotificationType.Error }),
            navigateTo({ path: redirectToPath }),
          ];
        }
        return [setTrialReservationInfo({ payload: trialReservation })];
      }),
    );
  });

  redeemTrial$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(redeemTrial),
      combineLatestWith(this.signalRFacade.isHubConnected$),
      switchMap(([payload]) => {
        const url = `${ApiEndPoints.Trial}/${payload.payload.campaignId}/reservations/${payload.payload.trialReservationId}/redeem`;
        const commandRequest = this.commandRequestBuilderService
          .new(
            url,
            'PUT', // eslint-disable-next-line wrap-iife
            (function (categoryName: CategoryName, eventType: string): (domainMessage: IDomainMessage) => boolean {
              return (domainMessage: IDomainMessage): boolean => {
                return domainMessage.type === eventType && domainMessage.categoryName === categoryName;
              };
            })('trialcampaignv2', TrialEvent.TrialRedeemed),
          )
          .withWaitOn202Accepted()
          .withErrorHandler({
            [HttpStatusCode.BadRequest]: this.errorHandlers.handleTrialErrors,
          });
        return combineLatest([commandRequest.invoke$(), of(payload.payload)]);
      }),
      switchMap(([response, redeemTrial]) => {
        if (response.error && response.errorActions) {
          return response.errorActions;
        }
        const entitlementPath =
          redeemTrial.trialReservationDetails.campaignType === 'user'
            ? this.USER_ENTITLEMENT_PATH
            : this.ORG_ENTITLEMENT_PATH;
        this.router.navigate([entitlementPath], {
          queryParams: { selectedTabIndex: 1 },
        });
        return [
          UiStateActions.displayMessage({
            payload: {
              message: 'Trial redeemed successfully! Trial has been added to your account',
              type: 'Success',
            },
          }),
          redeemTrialSuccess({ payload: redeemTrial }),
        ];
      }),
    );
  });

  allocateTrial$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(allocateTrial),
      switchMap((payload) => {
        const url = `${ApiEndPoints.Users}${payload.payload.userId}/trials/${payload.payload.trialId}/tenantallocate`;
        const commandRequest = this.commandRequestBuilderService
          .new(
            url,
            'PUT',
            (msg) =>
              msg.type === TrialEvent.TrialAllocated ||
              msg.type === TrialEvent.TrialAllocationFailed ||
              msg.type === TenantEvent.TrialAllocationUserActionPending,
          )
          .withTenantHeader(payload.payload.tenantId)
          .withWaitOn202Accepted();

        return combineLatest([commandRequest.invoke$(), of(payload), this.controlPageFacade.getAppsMap$]);
      }),
      switchMap(([response, payload, appsMap]) => {
        if (!response.error && response.message?.type === TrialEvent.TrialAllocated) {
          return [
            getTenantEffectiveRolesAndServices({ tenantId: String(payload.payload.tenantId) }),
            refreshTrialGrid(),
            UiStateActions.displayMessage({
              payload: {
                message: `${payload.payload.trialBundle.name} is now active on ${payload.payload.tenantName}.`,
                type: 'Success',
              },
            }),
            clearTrialGridRefresh(),
          ];
        } else if (response.message?.type === TrialEvent.TrialAllocationFailed) {
          let errorMsg = 'Trial could not be allocated.';
          const domainEventError = new DomainEvent(response.message);
          // REVISIT: Till the time back-end supports standard error format
          if (domainEventError.ErrorCode === AppErrorCode.TenantServiceProvisioningPending) {
            const appId = toAppId(domainEventError.ErrorCodeParts[1]);
            errorMsg = `${appsMap[appId]?.appName} needs further action to be enabled for your organization. Please open to complete.`;
            return [
              UiStateActions.displayMessage({
                payload: {
                  message: errorMsg,
                  type: 'Info',
                  customBtn: {
                    label: 'Go to Home',
                    navigateTo: this.DASHBOARD_PATH,
                  },
                },
              }),
            ];
          } else if (domainEventError.ErrorCode === AppErrorCode.EntitlementTransactionInProgress) {
            return [
              UiStateActions.displayMessage({
                payload: {
                  message: 'Service could not be allocated. Please try again.',
                  type: 'Error',
                },
              }),
            ];
          }
          return [
            UiStateActions.displayMessage({
              payload: {
                message: errorMsg,
                type: 'Error',
              },
            }),
          ];
        } else if (response.message?.type === TenantEvent.TrialAllocationUserActionPending) {
          const service = new DomainEvent<DomainResponseDataForTrialWithCSP>(response.message);
          const appId = service?.EventData.action.serviceKind;
          const serviceName = appsMap[appId]?.appName;
          const errorMsg = `${serviceName} needs further action to be enabled for your organization. Please open to complete.`;
          return [
            getTenantEffectiveRolesAndServices({ tenantId: String(payload.payload.tenantId) }),
            refreshTrialGrid(),
            UiStateActions.displayMessage({
              payload: {
                message: errorMsg,
                type: 'Info',
                customBtn: {
                  label: 'Go to Home',
                  navigateTo: this.DASHBOARD_PATH,
                },
              },
            }),
            clearTrialGridRefresh(),
          ];
        } else {
          return [
            UiStateActions.displayMessage({
              payload: {
                message: `Trial could not be allocated.`,
                type: 'Error',
              },
            }),
          ];
        }
      }),
    );
  });
}
